import {
    ChampionshipReigns,
    Championships,
    Factions, FactionsHash, FeudParticipantsHash, Feuds, Gimmicks,
    MatchContracts,
    Matches, MatchesHash, Negotiations,
    Properties, RandomEvents,
    Relationships, Rosters, StaffMembers,
    Streaks, Superstars, TagTeams, TagTeamsHash, TalentRequests, Tournaments
} from "./models.js";

function Superstar(name, popularity, age, partTimer = false, injured = 0, faceOfTheCompany = false,
                          graphic = null) {
    // Strings
    const injuryName = null;
    const activeEffect = null;
    const alignment = null;
    const brandName = null;
    const temporaryInjury = null;
    const highestRank = null;
    const objectName = 'superstar';
    const gender = 'male';

    // Booleans
    const unhappy = false;
    const retired = false;
    const dead = false;
    const hallOfFamer = false;
    const cyborg = false;
    const debutNewGimmick = false;
    const hot = false;
    const stale = false;
    const unavailable = false;
    const injuredAtLatestShow = null;
    const availableThisMonth = null;
    const fullTimeManager = null;
    const readyToReturn = null;
    const workableInjury = null;

    // Integer
    const faceOfTheCompanyCounter = 0;
    const morale = 65;
    const alignmentMonths = 0;
    const charisma = 1;
    const charismaXp = 0;
    const workrate = 1;
    const workrateXp = 0;
    const overness = 45;
    const winsCounter = 0;
    const lossesCounter = 0;
    const wins = 0;
    const losses = 0;
    const currentAttire = 0;
    const suspensionMonths = 0;
    const winsTemporary = 0;
    const lossesTemporary = 0;
    const kayfabePoints = 0;
    const kayfabePointsTemporary = 0;
    const fatigue = 0;
    const appearances = null;
    const tournamentSeed = null;
    const tournamentPoints = null;
    const monthToAppearIn = null;
    const injuryReturnMin = null;
    const injuryReturnMax = null;
    const contractMonthsRemaining = null;

    // Arrays
    const traits = [];

    // Objects
    const traitsProgress = {};

    // Foreign keys
    const rosterId = null;
    const originalRosterId = null;
    const propertyId = null;
    const offeredTradeOfferId = null;
    const requestedTradeOfferId = null;
    const managerId = null;

    return {
        name,
        objectName,
        popularity,
        age,
        partTimer,
        gender,
        injured,
        faceOfTheCompany,
        graphic,
        injuryName,
        activeEffect,
        alignment,
        brandName,
        temporaryInjury,
        highestRank,
        unhappy,
        retired,
        dead,
        hallOfFamer,
        cyborg,
        debutNewGimmick,
        hot,
        stale,
        unavailable,
        injuredAtLatestShow,
        availableThisMonth,
        fullTimeManager,
        readyToReturn,
        faceOfTheCompanyCounter,
        morale,
        alignmentMonths,
        charisma,
        charismaXp,
        workrate,
        workrateXp,
        overness,
        winsCounter,
        lossesCounter,
        wins,
        losses,
        currentAttire,
        suspensionMonths,
        winsTemporary,
        lossesTemporary,
        kayfabePoints,
        kayfabePointsTemporary,
        appearances,
        tournamentSeed,
        tournamentPoints,
        monthToAppearIn,
        injuryReturnMin,
        injuryReturnMax,
        contractMonthsRemaining,
        traits,
        traitsProgress,
        rosterId,
        originalRosterId,
        propertyId,
        offeredTradeOfferId,
        requestedTradeOfferId,
        managerId,
        workableInjury,
        fatigue,
    };
}

async function getSuperstarData(superstar) {
    let superstarData = JSON.parse(JSON.stringify(superstar));
    superstarData['championships'] = await getSuperstarChampionships(superstar);
    return superstarData;
}

function addSuperstarChampionships(superstar, championships) {
    championships.forEach(c => {
        c.superstarId = superstar.$loki
    });
}

function removeSuperstarChampionships(superstar, championships) {
    championships.forEach(c => {
        c.superstarId = null
    });
}

function getSuperstarChampionships(superstar) {
    return Championships.find({'superstarId': superstar.$loki});
}

async function getSuperstarTagTeams(superstar) {
    const tagTeamHashes = await TagTeamsHash.find({'superstarId': superstar.$loki});
    const tagTeamIds = tagTeamHashes.map(x => {
        return x.tagTeamId
    });
    return TagTeams.find({'$loki': {'$in': tagTeamIds}});
}

async function getSuperstarMatches(superstar) {
    const matchHashes = await MatchesHash.find({'superstarId': superstar.$loki});
    const matchIds = matchHashes.map(x => {
        return x.matchId
    });
    return Matches.find({'$loki': {'$in': matchIds}});
}

function getSuperstarRoster(superstar) {
    return Rosters.find({'$loki': superstar.rosterId})[0];
}

function getSuperstarOriginalRoster(superstar) {
    return Rosters.find({'$loki': superstar.originalRosterId})[0];
}

async function getSuperstarFactions(superstar) {
    const factionHashes = await FactionsHash.find({'superstarId': superstar.$loki});
    const factionIds = factionHashes.map(x => {
        return x.factionId
    });
    return Factions.find({'$loki': {'$in': factionIds}});
}

function getSuperstarLeadFactions(superstar) {
    return Factions.find({'leaderSuperstarId': superstar.$loki});
}

function addSuperstarChampionshipReigns(superstar, championshipReigns) {
    championshipReigns.forEach(c => {
        c.superstarId = superstar.$loki
    });
}

function removeSuperstarChampionshipReigns(superstar, championshipReigns) {
    championshipReigns.forEach(c => {
        c.superstarId = null
    });
}

function getSuperstarChampionshipReigns(superstar) {
    return ChampionshipReigns.find({'superstarId': superstar.$loki});
}

function getSuperstarProperty(superstar) {
    return Properties.find({'$loki': superstar.propertyId})[0];
}

function addSuperstarRelationships(superstar, relationships) {
    relationships.forEach(c => {
        c.superstarId = superstar.$loki
    });
}

function removeSuperstarRelationships(superstar, relationships) {
    relationships.forEach(c => {
        c.superstarId = null
    });
}

function getSuperstarRelationships(superstar) {
    return Relationships.find({'superstarId': superstar.$loki});
}

function addSuperstarMatchContracts(superstar, matchContracts) {
    matchContracts.forEach(c => {
        c.superstarId = superstar.$loki
    });
}

function removeSuperstarMatchContracts(superstar, matchContracts) {
    matchContracts.forEach(c => {
        c.superstarId = null
    });
}

function getSuperstarMatchContracts(superstar) {
    return MatchContracts.find({'superstarId': superstar.$loki});
}

function addSuperstarStreaks(superstar, streaks) {
    streaks.forEach(c => {
        c.superstarId = superstar.$loki
    });
}

function removeSuperstarStreaks(superstar, streaks) {
    streaks.forEach(c => {
        c.superstarId = null
    });
}

function getSuperstarStreaks(superstar) {
    return Streaks.find({'superstarId': superstar.$loki});
}

function addSuperstarWonTournaments(superstar, tournaments) {
    tournaments.forEach(c => {
        c.winnerSuperstarId = superstar.$loki
    });
}

function removeSuperstarWonTournaments(superstar, tournaments) {
    tournaments.forEach(c => {
        c.winnerSuperstarId = null
    });
}

function getSuperstarWonTournaments(superstar) {
    return Tournaments.find({'winnerSuperstarId': superstar.$loki});
}

function addSuperstarGimmicks(superstar, gimmicks) {
    gimmicks.forEach(c => {
        c.superstarId = superstar.$loki
    });
}

function removeSuperstarGimmicks(superstar, gimmicks) {
    gimmicks.forEach(c => {
        c.superstarId = null
    });
}

function getSuperstarGimmicks(superstar) {
    return Gimmicks.find({'superstarId': superstar.$loki});
}

function getSuperstarCurrentGimmick(superstar) {
    return Gimmicks.find({'superstarId': superstar.$loki}).reverse()[0];
}

function addSuperstarRandomEvents(superstar, randomEvents) {
    randomEvents.forEach(c => {
        c.superstarId = superstar.$loki
    });
}

function removeSuperstarRandomEvents(superstar, randomEvents) {
    randomEvents.forEach(c => {
        c.superstarId = null
    });
}

function getSuperstarRandomEvents(superstar) {
    return RandomEvents.find({'superstarId': superstar.$loki});
}

function addSuperstarStaffObjects(superstar, staff) {
    staff.forEach(c => {
        c.superstarObjectId = superstar.$loki
    });
}

function removeSuperstarStaffObjects(superstar, staff) {
    staff.forEach(c => {
        c.superstarObjectId = null
    });
}

function getSuperstarStaffObject(superstar) {
    return StaffMembers.find({'superstarObjectId': superstar.$loki})[0];
}

function addSuperstarNegotiations(superstar, negotiations) {
    negotiations.forEach(c => {
        c.superstarId = superstar.$loki
    });
}

function removeSuperstarNegotiations(superstar, negotiations) {
    negotiations.forEach(c => {
        c.superstarId = null
    });
}

function getSuperstarNegotiations(superstar) {
    return Negotiations.find({'superstarId': superstar.$loki});
}

function addSuperstarTalentRequests(superstar, talentRequests) {
    talentRequests.forEach(c => {
        c.superstarId = superstar.$loki
    });
}

function removeSuperstarTalentRequests(superstar, talentRequests) {
    talentRequests.forEach(c => {
        c.superstarId = null
    });
}

function getSuperstarTalentRequests(superstar) {
    return TalentRequests.find({'superstarId': superstar.$loki});
}

async function getSuperstarFeuds(superstar) {
    const feudHashes = await FeudParticipantsHash.find({'superstarId': superstar.$loki});
    const feudIds = feudHashes.map(x => {
        return x.feudId
    });
    return Feuds.find({'$loki': {'$in': feudIds}});
}

function setSuperstarManager(superstar, manager) {
    superstar.managerId = manager.$loki;
}

function removeSuperstarManager(superstar) {
    superstar.managerId = null;
}

function getSuperstarManager(superstar) {
    if (!superstar.managerId) {
        return
    }
    return Superstars.find({'$loki': superstar.managerId})[0];
}

function getSuperstarManagedSuperstars(superstar) {
    return Superstars.find({'managerId': superstar.$loki});
}

export { Superstar, getSuperstarData, addSuperstarChampionships, removeSuperstarChampionships, getSuperstarChampionships,
    getSuperstarTagTeams, getSuperstarMatches, getSuperstarRoster, getSuperstarOriginalRoster, getSuperstarLeadFactions,
    addSuperstarChampionshipReigns, removeSuperstarChampionshipReigns,
    getSuperstarChampionshipReigns, getSuperstarProperty, addSuperstarRelationships, removeSuperstarRelationships,
    getSuperstarRelationships, addSuperstarMatchContracts, removeSuperstarMatchContracts, getSuperstarMatchContracts,
    addSuperstarStreaks, removeSuperstarStreaks, getSuperstarStreaks, addSuperstarWonTournaments,
    removeSuperstarWonTournaments, getSuperstarWonTournaments, addSuperstarGimmicks, removeSuperstarGimmicks,
    getSuperstarGimmicks, addSuperstarRandomEvents, removeSuperstarRandomEvents, getSuperstarRandomEvents,
    addSuperstarStaffObjects, removeSuperstarStaffObjects, getSuperstarStaffObject, addSuperstarNegotiations,
    removeSuperstarNegotiations, getSuperstarNegotiations, addSuperstarTalentRequests, removeSuperstarTalentRequests,
    getSuperstarTalentRequests, getSuperstarFactions, getSuperstarFeuds, setSuperstarManager, removeSuperstarManager,
    getSuperstarManager, getSuperstarManagedSuperstars, getSuperstarCurrentGimmick };